import { Component } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import Layout from './components/Layout/Layout'
import ClientLayout from './components/ClientLayout/ClientLayout'
import Login from './pages/Auth/Login'
import ResetPassword from './pages/Auth/ResetPassword'
import Download from './pages/Download/Download'
import Dashboard from './pages/Dashboard/Dashboard'
import Progetti from './pages/Progetti/Progetti'
import Calendario from './pages/Calendario/Calendario'
import Rubrica from './pages/Rubrica/Rubrica'
import Creativi from './pages/Creativi/Creativi'
import Finance from './pages/Finance/Finance'
import Storage from './pages/Storage/Storage'
import Social from './pages/Social/Social'
import Note from './pages/Note/Note'
import Impostazioni from './pages/Impostazioni/Impostazioni'
import Team from './pages/Team/Team'
import Tasks from './pages/Tasks/Tasks'
import Chat from './pages/Chat/Chat'
import ClientDashboard from './pages/ClientDashboard/ClientDashboard'
import ClientImpostazioni from './pages/ClientImpostazioni/ClientImpostazioni'
import ClientFiles from './pages/ClientFiles/ClientFiles'
import Revisioni from './pages/Revisioni/Revisioni'
import ClientRevisioni from './pages/ClientRevisioni/ClientRevisioni'
import Preventivi from './pages/Preventivi/Preventivi'
import ClientPreventivi from './pages/ClientPreventivi/ClientPreventivi'
import Moodboard from './pages/Moodboard/Moodboard'
import Montati from './pages/Montati/Montati'
import Clienti from './pages/Clienti/Clienti'
import Invito from './pages/Invito/Invito'
import Tools from './pages/Tools/Tools'
import RevisionePubblica from './pages/RevisionePubblica/RevisionePubblica'
import Richiesta from './pages/Richiesta/Richiesta'
import Bacheca from './pages/Bacheca/Bacheca'
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy'
import CookieBanner from './components/CookieBanner/CookieBanner'
import NotFound from './pages/NotFound/NotFound'

/* ── React Error Boundary ── */
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(err) { return { error: err } }
  componentDidCatch(err, info) { console.error('[ErrorBoundary]', err, info) }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center px-6">
          <div className="max-w-md w-full bg-gray-900 border border-red-500/30 rounded-2xl p-6 text-center">
            <p className="text-red-400 font-semibold mb-2">Qualcosa è andato storto</p>
            <p className="text-gray-500 text-sm mb-4">{this.state.error?.message || 'Errore sconosciuto'}</p>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload() }}
              className="bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
            >
              Ricarica la pagina
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const Spinner = () => (
  <div className="min-h-screen bg-gray-950 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
  </div>
)

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth()
  if (loading) return <Spinner />
  return user ? children : <Navigate to="/login" />
}

/* Blocca accesso a route admin-only */
const AdminRoute = ({ children }) => {
  const { profile, loading } = useAuth()
  if (loading) return <Spinner />
  return profile?.role === 'admin' ? children : <Navigate to="/dashboard" replace />
}

/* Smista cliente vs membro al login */
const RootRedirect = () => {
  const { profile, loading } = useAuth()
  if (loading) return <Spinner />
  return profile?.role === 'client'
    ? <Navigate to="/client" replace />
    : <Navigate to="/dashboard" replace />
}

const AppRoutes = () => {
  const { user } = useAuth()
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/download" element={<Download />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/invito/:token" element={<Invito />} />
      <Route path="/r/:token" element={<RevisionePubblica />} />
      <Route path="/richiesta" element={<Richiesta />} />

      {/* Root → smista per ruolo */}
      <Route path="/" element={<PrivateRoute><RootRedirect /></PrivateRoute>} />

      {/* ── Area membro / team (Layout con sidebar completa) ── */}
      <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="bacheca" element={<Bacheca />} />
        <Route path="progetti" element={<Progetti />} />
        <Route path="calendario" element={<Calendario />} />
        <Route path="rubrica" element={<Rubrica />} />
        <Route path="creativi" element={<Creativi />} />
        <Route path="finance" element={<Finance />} />
        <Route path="storage" element={<Storage />} />
        <Route path="social" element={<Social />} />
        <Route path="note" element={<Note />} />
        <Route path="team" element={<Team />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="chat" element={<Chat />} />
        <Route path="moodboard" element={<Moodboard />} />
        <Route path="montati" element={<Montati />} />
        <Route path="tools" element={<Tools />} />
        <Route path="revisioni" element={<Revisioni />} />
        <Route path="preventivi" element={<AdminRoute><Preventivi /></AdminRoute>} />
        <Route path="clienti" element={<AdminRoute><Clienti /></AdminRoute>} />
        <Route path="impostazioni" element={<Impostazioni />} />
      </Route>

      {/* ── Area cliente (layout slim) ── */}
      <Route path="/client" element={<PrivateRoute><ClientLayout /></PrivateRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="progetti" element={<Progetti />} />
        <Route path="revisioni" element={<ClientRevisioni />} />
        <Route path="preventivi" element={<ClientPreventivi />} />
        <Route path="files" element={<ClientFiles />} />
        <Route path="chat" element={<Chat />} />
        <Route path="impostazioni" element={<ClientImpostazioni />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <CookieBanner />
      </AuthProvider>
    </BrowserRouter>
  </ErrorBoundary>
)

export default App
